import React from 'react';
import { List, RootRef, Typography } from '@material-ui/core';
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
  DraggableId,
} from '@campfire/draggable-list';
import { FormListItemContext } from './FormListItemContext';
import { FormListItem } from './FormListItem';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

type FlatItem = {
  id: string;
  groupId?: string;
};

type RenderChunk =
  | { type: 'item'; flatIndex: number; item: FlatItem }
  | {
      type: 'group';
      groupId: string;
      headerIndex: number;
      headerId: string;
      children: Array<{ flatIndex: number; item: FlatItem }>;
    };

function buildFlatList(
  allIds: string[],
  byGroup: { [groupId: string]: string[] },
  byTriggerOption: { [id: string]: string }
): FlatItem[] {
  const flat: FlatItem[] = [];
  allIds.forEach((id) => {
    if (byTriggerOption[id]) return;
    flat.push({ id });
    const children = byGroup[id];
    if (children) {
      children.forEach((childId) => {
        flat.push({ id: childId, groupId: id });
      });
    }
  });
  return flat;
}

function buildRenderChunks(
  flatList: FlatItem[],
  byGroup: { [groupId: string]: string[] },
  byTriggerOption: { [id: string]: string }
): RenderChunk[] {
  const chunks: RenderChunk[] = [];
  let currentGroup: RenderChunk | null = null;
  let flatIndex = 0;

  flatList.forEach((fi) => {
    if (byTriggerOption[fi.id]) {
      flatIndex++;
      return;
    }

    const isGroupHeader = byGroup[fi.id] !== undefined;

    if (isGroupHeader) {
      currentGroup = {
        type: 'group',
        groupId: fi.id,
        headerIndex: flatIndex,
        headerId: fi.id,
        children: [],
      };
      chunks.push(currentGroup);
      flatIndex++;
    } else if (fi.groupId && currentGroup && currentGroup.type === 'group' && currentGroup.groupId === fi.groupId) {
      currentGroup.children.push({ flatIndex, item: fi });
      flatIndex++;
    } else {
      currentGroup = null;
      chunks.push({ type: 'item', flatIndex, item: fi });
      flatIndex++;
    }
  });

  return chunks;
}

function reconstructFromFlat(
  flatList: FlatItem[],
  byGroup: { [groupId: string]: string[] },
  byGroupItem: { [id: string]: string },
  byId: { [id: string]: { __typename?: string } },
  draggedId: string
): { allIds: string[]; byGroup: { [k: string]: string[] }; byGroupItem: { [k: string]: string } } {
  const newAllIds: string[] = [];
  const newByGroup: { [k: string]: string[] } = {};
  const newByGroupItem: { [k: string]: string } = {};

  Object.keys(byGroup).forEach((gid) => {
    newByGroup[gid] = [];
  });

  let currentGroupId: string | undefined;

  flatList.forEach((item) => {
    const isGroupHeader = byGroup[item.id] !== undefined;
    const canBeGroupChild = byId[item.id]?.__typename === 'VOLUNTEER_FormItemFieldType';

    if (isGroupHeader) {
      newAllIds.push(item.id);
      currentGroupId = item.id;
      if (!newByGroup[item.id]) {
        newByGroup[item.id] = [];
      }
    } else if (currentGroupId && canBeGroupChild) {
      const wasChildOfCurrentGroup = byGroupItem[item.id] === currentGroupId;
      const isDraggedItem = item.id === draggedId;

      if (wasChildOfCurrentGroup || isDraggedItem) {
        newByGroup[currentGroupId].push(item.id);
        newByGroupItem[item.id] = currentGroupId;
      } else {
        currentGroupId = undefined;
        newAllIds.push(item.id);
      }
    } else {
      currentGroupId = undefined;
      newAllIds.push(item.id);
    }
  });

  return { allIds: newAllIds, byGroup: newByGroup, byGroupItem: newByGroupItem };
}

export function FormItemList() {
  const { formItems, setFieldValue, setDraggingId } = React.useContext(FormListItemContext);
  const { allIds, byTriggerOption } = formItems;
  const { theme } = useCampfireTheme();

  const flatList = React.useMemo(() => buildFlatList(allIds, formItems.byGroup, byTriggerOption), [
    allIds,
    formItems.byGroup,
    byTriggerOption,
  ]);

  const renderChunks = React.useMemo(() => buildRenderChunks(flatList, formItems.byGroup, byTriggerOption), [
    flatList,
    formItems.byGroup,
    byTriggerOption,
  ]);

  const onDragEnd = (result: DropResult) => {
    setDraggingId('');
    const { droppableId: destinationItemId, index: destinationIndex = 0 } = result.destination || {};
    const { droppableId: sourceItemId, index: sourceIndex } = result.source || {};
    const { draggableId } = result;

    if (!sourceItemId || !destinationItemId) {
      return;
    }

    if (sourceItemId === 'root' && destinationItemId === 'root') {
      const isGroupHeader = formItems.byGroup[draggableId] !== undefined;
      const groupChildren = isGroupHeader ? formItems.byGroup[draggableId] || [] : [];
      const idsToMove = isGroupHeader ? [draggableId, ...groupChildren] : [draggableId];
      const idsToMoveSet = new Set(idsToMove);

      const remaining = flatList.filter((item) => !idsToMoveSet.has(item.id));

      let adjustedDest = destinationIndex;
      flatList.forEach((item, i) => {
        if (idsToMoveSet.has(item.id) && i < destinationIndex) {
          adjustedDest--;
        }
      });

      const insertItems: FlatItem[] = [{ id: draggableId }];
      if (isGroupHeader) {
        groupChildren.forEach((childId) => {
          insertItems.push({ id: childId, groupId: draggableId });
        });
      }

      const reordered = [...remaining.slice(0, adjustedDest), ...insertItems, ...remaining.slice(adjustedDest)];

      const { allIds: newAllIds, byGroup: newByGroup, byGroupItem: newByGroupItem } = reconstructFromFlat(
        reordered,
        formItems.byGroup,
        formItems.byGroupItem,
        formItems.byId,
        draggableId
      );

      setFieldValue('formItems', {
        ...formItems,
        allIds: newAllIds,
        byGroup: newByGroup,
        byGroupItem: newByGroupItem,
      });
      return;
    }

    if (sourceItemId === 'root' && destinationItemId !== 'root') {
      // A page break cannot live inside a conditional block: the items it would
      // separate are only ever rendered alongside the option that reveals them.
      if (formItems.byId[draggableId]?.__typename === 'VOLUNTEER_FormItemPageBreakType') {
        return;
      }
      const itemIds = (formItems.byTrigger[destinationItemId]?.triggerItemIds ?? []).slice();
      itemIds.splice(destinationIndex, 0, draggableId);
      setFieldValue('formItems', {
        ...formItems,
        byTrigger: {
          ...formItems.byTrigger,
          [destinationItemId]: {
            ...formItems.byTrigger[destinationItemId],
            triggerItemIds: itemIds,
          },
        },
        byTriggerOption: {
          ...formItems.byTriggerOption,
          [draggableId]: destinationItemId,
        },
      });
      return;
    }

    if (sourceItemId !== 'root' && destinationItemId !== 'root') {
      const sourceItemIds = (formItems.byTrigger[sourceItemId]?.triggerItemIds ?? []).slice();
      sourceItemIds.splice(sourceIndex, 1);
      if (sourceItemId === destinationItemId) {
        sourceItemIds.splice(destinationIndex, 0, draggableId);
        setFieldValue('formItems', {
          ...formItems,
          byTrigger: {
            ...formItems.byTrigger,
            [destinationItemId]: {
              ...formItems.byTrigger[destinationItemId],
              triggerItemIds: sourceItemIds,
            },
          },
          byTriggerOption: {
            ...formItems.byTriggerOption,
            [draggableId]: destinationItemId,
          },
        });
      } else {
        const destinationItemIds = (formItems.byTrigger[destinationItemId]?.triggerItemIds ?? []).slice();
        destinationItemIds.splice(destinationIndex, 0, draggableId);
        setFieldValue('formItems', {
          ...formItems,
          byTrigger: {
            ...formItems.byTrigger,
            [sourceItemId]: {
              ...formItems.byTrigger[sourceItemId],
              triggerItemIds: sourceItemIds,
            },
            [destinationItemId]: {
              ...formItems.byTrigger[destinationItemId],
              triggerItemIds: destinationItemIds,
            },
          },
          byTriggerOption: {
            ...formItems.byTriggerOption,
            [draggableId]: destinationItemId,
          },
        });
      }
    }
  };
  const onBeforeCapture = ({ draggableId }: { draggableId: DraggableId }) => {
    setDraggingId(draggableId);
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd} onBeforeCapture={onBeforeCapture}>
        <Droppable droppableId='root'>
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
            const style = { paddingBottom: '2rem' };
            const updatedStyle = snapshot.isDraggingOver ? { pointerEvents: 'none', ...style } : style;
            return (
              <RootRef rootRef={provided.innerRef}>
                <List style={updatedStyle as React.CSSProperties}>
                  {renderChunks.map((chunk) => {
                    if (chunk.type === 'item') {
                      if (byTriggerOption[chunk.item.id]) {
                        return <div style={{ display: 'none' }} key={chunk.item.id} />;
                      }
                      return <FormListItem formItemId={chunk.item.id} index={chunk.flatIndex} key={chunk.item.id} />;
                    }

                    const cardWrapperStyle: React.CSSProperties = {
                      borderLeft: `1px solid ${theme.color.grey.border}`,
                      borderRight: `1px solid ${theme.color.grey.border}`,
                      borderBottom: `1px solid ${theme.color.grey.border}`,
                      borderBottomLeftRadius: 4,
                      borderBottomRightRadius: 4,
                      backgroundColor: '#fff',
                      padding: '0 16px 16px 16px',
                      marginBottom: 8,
                    };

                    const dashedBoxStyle: React.CSSProperties = {
                      minHeight: '80px',
                      borderRadius: '6px',
                      color: '#979797',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: chunk.children.length > 0 ? 'flex-start' : 'center',
                      fontSize: '12px',
                      border: '2px dashed #979797',
                      padding: '8px',
                      backgroundColor: '#fff',
                    };

                    return (
                      <React.Fragment key={chunk.groupId}>
                        <FormListItem formItemId={chunk.headerId} index={chunk.headerIndex} />
                        <div style={cardWrapperStyle}>
                          <div style={dashedBoxStyle}>
                            {chunk.children.length > 0 ? (
                              chunk.children.map((child) => (
                                <FormListItem
                                  formItemId={child.item.id}
                                  index={child.flatIndex}
                                  key={child.item.id}
                                  isGroupChild
                                />
                              ))
                            ) : (
                              <Typography variant='body2' style={{ color: '#979797', fontStyle: 'italic' }}>
                                Drag and drop fields here
                              </Typography>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {provided.placeholder}
                </List>
              </RootRef>
            );
          }}
        </Droppable>
      </DragDropContext>
    </>
  );
}
